/**
 * Telemetry module for the Streamko web app.
 *
 * Backed by OpenTelemetry (see `otel.ts`); custom events, exceptions, page
 * views and timed operations are emitted as OTLP spans to the same collector
 * the backend uses. The public surface is unchanged from the previous
 * Application Insights implementation. Every call is no-throw and degrades to
 * a no-op span when no collector is configured (dev / observability disabled).
 */
import { SpanStatusCode, type Attributes, type Span } from '@opentelemetry/api';
import { getWebTracer } from './otel';

const isDev = import.meta.env.DEV;

/** Emit a short-lived span; never throws (telemetry must not break the UI). */
function emitSpan(name: string, attributes?: Attributes, decorate?: (span: Span) => void): void {
  try {
    const span = getWebTracer().startSpan(name, attributes ? { attributes } : undefined);
    decorate?.(span);
    span.end();
  } catch {
    /* swallow — telemetry must never block the UI */
  }
}

/** Track a custom event with optional properties and measurements */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>,
): void {
  emitSpan(name, { ...properties, ...measurements });

  if (isDev) {
    console.debug('[telemetry] event:', name, properties, measurements);
  }
}

/** Track an exception */
export function trackException(error: Error, properties?: Record<string, string>): void {
  emitSpan('exception', properties, (span) => {
    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
  });

  if (isDev) {
    console.debug('[telemetry] exception:', error.message, properties);
  }
}

/** Track a page view */
export function trackPageView(name: string, uri?: string): void {
  emitSpan('page_view', { 'page.name': name, ...(uri ? { 'page.url': uri } : {}) });
}

/**
 * Start a timed trace operation (a real span). Call the returned `end()` when
 * the operation completes.
 *
 * Usage:
 *   const span = startTrace('wizard.locationStep');
 *   try {
 *     await doWork();
 *     span.end({ success: 'true' });
 *   } catch (e) {
 *     span.end({ success: 'false', error: e.message });
 *   }
 */
export function startTrace(name: string, properties?: Record<string, string>) {
  const startTime = performance.now();
  let span: Span | null = null;
  try {
    span = getWebTracer().startSpan(name, properties ? { attributes: properties } : undefined);
  } catch {
    /* ignore */
  }

  return {
    end(extraProperties?: Record<string, string>) {
      const duration = Math.round(performance.now() - startTime);
      try {
        span?.setAttributes({ ...extraProperties, durationMs: duration });
        span?.end();
      } catch {
        /* ignore */
      }
    },
  };
}

// ── Wizard-specific telemetry helpers ──────────────────────────────────

export function trackWizardStepComplete(step: string, durationMs?: number): void {
  trackEvent('wizard.stepComplete', { step }, durationMs != null ? { durationMs } : undefined);
}

export function trackWizardComplete(totalDurationMs?: number): void {
  trackEvent('wizard.complete', undefined, totalDurationMs != null ? { totalDurationMs } : undefined);
}

export function trackWizardAbandoned(lastStep: string): void {
  trackEvent('wizard.abandoned', { lastStep });
}

export function trackSignupAttempt(method: 'email' | 'google' | 'microsoft' | 'apple'): void {
  trackEvent('signup.attempt', { method });
}

export function trackSignupSuccess(method: 'email' | 'google' | 'microsoft' | 'apple'): void {
  trackEvent('signup.success', { method });
}

export function trackSignupError(method: string, error: string): void {
  trackEvent('signup.error', { method, error });
}
